import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CardActionArea,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PeopleIcon from "@mui/icons-material/People";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import LocalParkingIcon from "@mui/icons-material/LocalParking";
import BuildIcon from "@mui/icons-material/Build";
import HandymanIcon from "@mui/icons-material/Handyman";
import BusinessIcon from "@mui/icons-material/Business";
import { Inventory2, Person, Route, School } from "@mui/icons-material";
import { usePlanStatus } from "../../hooks/usePlanStatus";
import { OperationType } from "../../types/operationType";

const allMasterDataModules = [
  {
    title: "Clients",
    description: "Manage clients",
    icon: <BusinessIcon sx={{ fontSize: 25 }} />,
    path: "/master-data/clients",
    color: "#26c6da",
    operationTypes: [
      OperationType.CONTRACT,
      OperationType.SCHOOL,
      OperationType.LARGE_FLEET,
      OperationType.LARGE_FLEET_ALL,
      OperationType.ALL,
    ],
    hasPermission: (permissions: any) => permissions?.canViewClient || false,
  },
  {
    title: "Petrol Pumps",
    description: "Manage fuel station partners",
    icon: <LocalGasStationIcon sx={{ fontSize: 25 }} />,
    path: "/master-data/petrol-pumps",
    color: "#00acc1",
    operationTypes: [
      OperationType.CONTRACT,
      OperationType.SCHOOL,
      OperationType.DAILY_BUS,
      OperationType.TOUR_BOOKING,
      OperationType.LARGE_FLEET,
      OperationType.LARGE_FLEET_ALL,
      OperationType.ALL,
    ],
    hasPermission: (permissions: any) =>
      permissions?.canViewPetrolPump || false,
  },
  {
    title: "Parking Locations",
    description: "Manage parking locations and rates",
    icon: <LocalParkingIcon sx={{ fontSize: 25 }} />,
    path: "/master-data/parkings",
    color: "#00bcd4",
    operationTypes: [
      OperationType.CONTRACT,
      OperationType.SCHOOL,
      OperationType.DAILY_BUS,
      OperationType.TOUR_BOOKING,
      OperationType.LARGE_FLEET,
      OperationType.LARGE_FLEET_ALL,
      OperationType.ALL,
    ],
    hasPermission: (permissions: any) =>
      permissions?.canViewParkingLocation || false,
  },
  {
    title: "Garage & Service Centers",
    description: "Manage vehicle service centers",
    icon: <BuildIcon sx={{ fontSize: 25 }} />,
    path: "/master-data/garages",
    color: "#26c6da",
    operationTypes: [
      OperationType.CONTRACT,
      OperationType.SCHOOL,
      OperationType.DAILY_BUS,
      OperationType.TOUR_BOOKING,
      OperationType.LARGE_FLEET,
      OperationType.LARGE_FLEET_ALL,
      OperationType.ALL,
    ],
    hasPermission: (permissions: any) => permissions?.canViewGarage || false,
  },
  {
    title: "Mechanics",
    description: "Manage mechanics",
    icon: <HandymanIcon sx={{ fontSize: 25 }} />,
    path: "/master-data/mechanics",
    color: "#26c6da",
    operationTypes: [
      OperationType.CONTRACT,
      OperationType.SCHOOL,
      OperationType.DAILY_BUS,
      OperationType.TOUR_BOOKING,
      OperationType.LARGE_FLEET,
      OperationType.LARGE_FLEET_ALL,
      OperationType.ALL,
    ],
    hasPermission: (permissions: any) => permissions?.canViewMechanic || false,
  },
  {
    title: "Fuel Card",
    description: "Manage fuel card",
    icon: <LocalGasStationIcon sx={{ fontSize: 25 }} />,
    path: "/master-data/fuel-card",
    color: "#26c6da",
    operationTypes: [
      OperationType.CONTRACT,
      OperationType.SCHOOL,
      OperationType.DAILY_BUS,
      OperationType.TOUR_BOOKING,
      OperationType.LARGE_FLEET,
      OperationType.LARGE_FLEET_ALL,
      OperationType.ALL,
    ],
    hasPermission: (permissions: any) => permissions?.canViewFuelCard || false,
  },
  {
    title: "Inventory",
    description: "Manage inventory",
    icon: <Inventory2 sx={{ fontSize: 25 }} />,
    path: "/master-data/inventory",
    color: "#26c6da",
    operationTypes: [
      OperationType.CONTRACT,
      OperationType.SCHOOL,
      OperationType.DAILY_BUS,
      OperationType.TOUR_BOOKING,
      OperationType.LARGE_FLEET,
      OperationType.LARGE_FLEET_ALL,
      OperationType.ALL,
    ],
    hasPermission: (permissions: any) => permissions?.canViewFuelCard || false, // todo create permission for inventory
  },
];

// Function to filter modules based on operation type and permissions
const getFilteredModules = (operationType: OperationType, permissions: any) => {
  return allMasterDataModules.filter((module) => {
    // First check if module is available for current operation type
    const isAvailableForOperation =
      module.operationTypes.includes(operationType);

    // Then check if user has permission to view this module
    const hasPermission = module.hasPermission(permissions);

    return isAvailableForOperation && hasPermission;
  });
};

const MasterData: React.FC = () => {
  const navigate = useNavigate();
  const { planStatus } = usePlanStatus();

  // Get current operation type and filter modules accordingly
  const currentOperationType = planStatus.company?.operationType;
  const masterDataModules = getFilteredModules(
    currentOperationType,
    planStatus?.permissions
  );

  return (
    <>
      <Typography
        variant="h4"
        gutterBottom
        component="h1"
        sx={{ mb: 2, fontSize: "1.2rem" }}
      >
        Master Data Management
      </Typography>

      {masterDataModules.length > 0 ? (
        <Grid container spacing={3}>
          {masterDataModules.map((module) => (
            <Grid item xs={12} sm={6} md={2.4} key={module.path}>
              <Card
                sx={{
                  height: "100%",
                  transition: "transform 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 3,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => {
                    const path: string = module.path;
                    if (path) {
                      navigate(path);
                    }
                  }}
                  sx={{ height: "100%" }}
                >
                  <CardContent
                    sx={{
                      textAlign: "center",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      p: 2,
                    }}
                  >
                    <Box
                      sx={{
                        mb: 1,
                        p: 1,
                        borderRadius: "50%",
                        backgroundColor: `${module.color}15`,
                        color: module.color,
                      }}
                    >
                      {module.icon}
                    </Box>
                    <Typography
                      gutterBottom
                      variant="h6"
                      component="h2"
                      fontSize={14}
                    >
                      {module.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontSize={10}
                    >
                      {module.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            No Master Data Modules Available
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            You don't have permission to view any master data modules. Please
            contact your administrator for access.
          </Typography>
        </Box>
      )}
    </>
  );
};

export default MasterData;
